"use client";

import { useEffect } from "react";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { clarityStop } from "@/lib/clarity";

/**
 * ClientProviders — THE single client boundary at the root of the app.
 *
 * WHERE A NEW PROVIDER GOES
 * -------------------------
 * Put it HERE if it needs any of: React hooks, browser APIs (`window`,
 * `document`, `localStorage`), event handlers, or a `dynamic(…, { ssr: false })`
 * import. Put it in `components/providers/ServerProviders.tsx` instead if it is
 * server-composable — static markup, JSON-LD, or anything that fetches data on
 * the server. Default to ServerProviders; only what genuinely needs client
 * interactivity belongs in this file.
 *
 * WHY `children` IS A PROP AND MUST STAY ONE
 * -----------------------------------------
 * `ServerProviders` renders `<ClientProviders>{children}</ClientProviders>`, so
 * the page tree arrives here as an already-rendered RSC slot. That is the entire
 * mechanism keeping the storefront server-rendered: this file carries
 * `"use client"`, but a client component's `children` PROP is not pulled into
 * the client boundary. If this file ever *imported* the page content instead of
 * receiving it, every storefront page would become client-rendered.
 *
 * WHY THE `ssr: false` LOADERS BELOW ARE LOAD-BEARING
 * ---------------------------------------------------
 * On OpenNext/Cloudflare Workers, client components are still compiled INTO the
 * Worker for SSR — a `"use client"` directive does NOT exclude code from the
 * server bundle. `ssr: false` is the only thing that does. Removing one grows
 * `handler.mjs`, which costs cold-start parse CPU on a flat CPU-ms budget.
 * None of these produce SEO-visible HTML.
 */

const ServiceWorkerRegistrar = dynamic(
  () => import("@/components/pwa/ServiceWorkerRegistrar").then(m => ({ default: m.ServiceWorkerRegistrar })),
  { ssr: false },
);
const GatedGoogleAnalytics = dynamic(
  () => import("@/components/analytics/GatedGoogleAnalytics").then(m => ({ default: m.GatedGoogleAnalytics })),
  { ssr: false },
);
const GatedMicrosoftClarity = dynamic(
  () => import("@/components/analytics/GatedMicrosoftClarity").then(m => ({ default: m.GatedMicrosoftClarity })),
  { ssr: false },
);
const CookieConsentBanner = dynamic(
  () => import("@/components/cookie/cookie-consent").then(m => ({ default: m.CookieConsentBanner })),
  { ssr: false },
);
const Toaster = dynamic(
  () => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })),
  { ssr: false },
);
// Custom error reporter — captures uncaught errors + unhandled rejections
// and ships them to /api/client-errors → app_errors table → /admin/errors.
const ClientErrorReporter = dynamic(
  () => import("@/components/ClientErrorReporter").then(m => ({ default: m.ClientErrorReporter })),
  { ssr: false },
);

export function ClientProviders({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  // Never load analytics / session-recording on the private admin + florist
  // portals: they're internal staff tools (a privacy concern to record), and
  // their pageviews would otherwise pollute the storefront traffic analytics
  // (/admin/analytics). The service worker still registers everywhere (admin
  // push needs it). usePathname is stable server↔client, so no hydration risk.
  const isPrivateArea = pathname.startsWith("/admin") || pathname.startsWith("/florist");

  // Not rendering the loader is only half the gate. Once Clarity's snippet has
  // run on a storefront page it keeps recording across client-side navigation
  // on its own history hook, so unmounting the component below does NOT stop a
  // staff member who browsed the shop and then clicked into the dashboard.
  // Measured consequence: /admin, /admin/orders and /admin/analytics were being
  // recorded and counted as customer sessions — 15 of one day's 60 — which made
  // every number on /admin/behaviour describe the owner as much as the customer.
  // `clarityStop` is a no-op when the tag never loaded.
  useEffect(() => {
    if (isPrivateArea) clarityStop();
  }, [isPrivateArea]);

  return (
    <>
      <ServiceWorkerRegistrar />
      {!isPrivateArea && <GatedGoogleAnalytics />}
      {!isPrivateArea && <GatedMicrosoftClarity />}
      <ClientErrorReporter />
      <Toaster />
      {/* TOOLTIPROOT-1 — there is deliberately NO <TooltipProvider> here.
          This component is mounted by the ROOT layout, so a provider here sat in
          the critical path of every page on the site while having ZERO consumers:
          the only two files that render a Tooltip each mount their OWN provider
          with their own delay — components/ui/sidebar.tsx (delayDuration={0}) and
          app/(admin)/admin/notifications/failed/FailedNotificationsClient.tsx
          (delayDuration={150}). Adding one back would pull @radix-ui/react-tooltip
          into the storefront root render for nothing. If a storefront tooltip is
          ever needed, wrap THAT subtree, not the whole site. */}
      {children}
      {/* Storefront-only: the consent banner exists to gate analytics, which is
          already disabled on staff routes (isPrivateArea) — so on /admin and
          /florist it was pure intrusion (it overlapped the sidebar + stole
          mobile space). Gate it off staff areas, matching GA/Clarity above. */}
      {!isPrivateArea && <CookieConsentBanner />}
    </>
  );
}
